<script lang="ts">
	import type { Snippet } from "svelte";
	import { Section, SectionHeader } from "#lib/web-ui/primitives/index.js";
	import Skeleton from "#lib/web-ui/ui/skeleton/skeleton.svelte";
	import PlayIcon from "@lucide/svelte/icons/play";
	import PauseIcon from "@lucide/svelte/icons/pause";
	import HeadphonesIcon from "@lucide/svelte/icons/headphones";
	import ExternalLinkIcon from "@lucide/svelte/icons/external-link";
	import { podcastState } from "./podcast-state.svelte";
	import type { PodcastSectionProps, PodcastEpisode } from "./podcast.types";

	let {
		label,
		title,
		subtitle,
		episodes,
		theme = "muted",
		class: className,
		footer
	}: PodcastSectionProps & { footer?: Snippet } = $props();

	function playEpisode(episode: PodcastEpisode) {
		podcastState.playEpisode(episode);
	}

	function isCurrentlyPlaying(episode: PodcastEpisode): boolean {
		return podcastState.currentEpisode?.id === episode.id && podcastState.isPlaying;
	}

	function formatDuration(seconds: number): string {
		const minutes = Math.floor(seconds / 60);
		return `${minutes} min`;
	}

	function formatDate(dateStr: string): string {
		return new Date(dateStr).toLocaleDateString("en-US", {
			month: "short",
			day: "numeric",
			year: "numeric"
		});
	}
</script>

<Section {theme} class={className}>
	<SectionHeader {label} {title} {subtitle} />

	<div class="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
		{#if episodes.length === 0}
			{#each Array(3) as _}
				<div class="bg-card flex flex-col overflow-hidden rounded border shadow-sm">
					<Skeleton class="aspect-[16/9] w-full rounded-none" />
					<div class="flex flex-col gap-2 p-4">
						<div class="flex items-center gap-2">
							<Skeleton class="h-3 w-3 rounded-full" />
							<Skeleton class="h-3 w-28" />
							<Skeleton class="h-3 w-16" />
						</div>
						<Skeleton class="h-4 w-full" />
						<Skeleton class="h-4 w-2/3" />
						<div class="mt-2 flex items-center gap-2">
							<Skeleton class="h-7 w-16 rounded" />
							<Skeleton class="h-7 w-16 rounded" />
							<Skeleton class="h-7 w-16 rounded" />
						</div>
					</div>
				</div>
			{/each}
		{/if}
		{#each episodes as episode (episode.id)}
			{@const playing = isCurrentlyPlaying(episode)}
			<div
				class="bg-card group flex flex-col overflow-hidden rounded border shadow-sm transition-shadow hover:shadow-md"
			>
				<!-- Artwork -->
				<div class="relative aspect-[16/9] overflow-hidden">
					<img
						src={episode.imageUrl}
						alt={episode.channelTitle}
						class="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
						width="400"
						height="225"
						loading="lazy"
					/>
					<!-- Play overlay on hover -->
					<button
						onclick={() => playEpisode(episode)}
						aria-label="Play {episode.channelTitle}"
						class="absolute inset-0 flex items-center justify-center bg-black/0 transition-colors group-hover:bg-black/30"
					>
						<span
							class="bg-primary text-primary-foreground scale-90 rounded-full p-3 opacity-0 shadow-lg transition-all group-hover:scale-100 group-hover:opacity-100"
						>
							{#if playing}
								<PauseIcon class="h-5 w-5" />
							{:else}
								<PlayIcon class="ml-0.5 h-5 w-5" />
							{/if}
						</span>
					</button>
					<!-- Duration badge -->
					<span
						class="absolute right-2 bottom-2 rounded bg-black/70 px-2 py-0.5 text-xs font-medium text-white"
					>
						{formatDuration(episode.durationSeconds)}
					</span>
				</div>

				<!-- Content -->
				<div class="flex grow flex-col p-4">
					<div class="mb-2 flex items-center gap-2 text-xs">
						<HeadphonesIcon class="text-muted-foreground h-3 w-3" />
						<span class="text-muted-foreground font-medium">{episode.channelTitle}</span
						>
						<span class="text-muted-foreground">·</span>
						<span class="text-muted-foreground">{formatDate(episode.publishedAt)}</span>
					</div>

					<h3
						class="text-card-foreground mb-3 line-clamp-2 text-sm leading-snug font-semibold"
					>
						{episode.title}
					</h3>

					<!-- Actions -->
					<div class="mt-auto flex items-center gap-2">
						<button
							onclick={() => playEpisode(episode)}
							class="bg-primary text-primary-foreground inline-flex items-center gap-1.5 rounded px-3 py-1.5 text-xs font-medium transition-colors hover:opacity-90"
						>
							{#if playing}
								<PauseIcon class="h-3 w-3" />
								Playing
							{:else}
								<PlayIcon class="h-3 w-3" />
								Play
							{/if}
						</button>

						{#if episode.itunesUrl}
							<a
								href={episode.itunesUrl}
								target="_blank"
								rel="noopener noreferrer"
								aria-label="Listen to {episode.title} on Apple Podcasts"
								class="text-muted-foreground hover:text-card-foreground inline-flex items-center gap-1 rounded border px-2 py-1.5 text-xs transition-colors"
							>
								<ExternalLinkIcon class="h-3 w-3" />
								Apple
							</a>
						{/if}

						{#if episode.spotifyUrl}
							<a
								href={episode.spotifyUrl}
								target="_blank"
								rel="noopener noreferrer"
								aria-label="Listen to {episode.title} on Spotify"
								class="text-muted-foreground hover:text-card-foreground inline-flex items-center gap-1 rounded border px-2 py-1.5 text-xs transition-colors"
							>
								<ExternalLinkIcon class="h-3 w-3" />
								Spotify
							</a>
						{/if}
					</div>
				</div>
			</div>
		{/each}
	</div>

	{#if footer}
		{@render footer()}
	{/if}
</Section>
